import type WaTimeInput from '@awesome.me/webawesome-pro/dist/components/time-input/time-input.js'
import type { ICellEditorComp, ICellEditorParams } from 'ag-grid-community'
import {
  combineDateTimeValue,
  configuredTemporalStep,
  datePart,
  hasValidDateShape,
  hasValidTimeShape,
  inferTemporalPrecision,
  isTimeStepAligned,
  isTimeWithinBounds,
  isWithinBounds,
  matchingDateTimeBound,
  normalizeTime,
  preserveTimeMilliseconds,
  type TemporalPrecision,
  type TemporalPrecisionConfig,
  timePart
} from '../../../helpers/temporal'
import { loadPageComponents } from '../../../helpers/webawesome-components'
import { iconElement } from '../utils/dom'
import {
  extractEditorValue,
  type ParsedEditorValue,
  parsedEditorValue
} from '../utils/editor-value'

const DATE_INPUT_TAG: string = 'wa-date-input'

type DateInputElement = HTMLElement & {
  value: string
  required: boolean
  withClear: boolean
  min: string
  max: string
  size: 's'
  readonly checkValidity: () => boolean
  readonly updateComplete: Promise<boolean>
  readonly show: () => Promise<void>
  readonly hide: () => Promise<void>
  readonly focus: (options?: FocusOptions) => void
}

type TemporalEditorParams = ICellEditorParams<
  Record<string, unknown>,
  unknown,
  unknown
> & {
  readonly required?: boolean
  readonly min?: string
  readonly max?: string
  readonly precision?: TemporalPrecisionConfig
}

const stringValue = (value: unknown): string =>
  typeof value === 'string' ? value : ''

const nullableValue = (value: string): string | null =>
  value.length > 0 ? value : null

// Compares local timestamps at the same precision without timezone conversion.
const comparableDateTime = (value: string | null | undefined): string =>
  combineDateTimeValue(
    datePart(value ?? ''),
    timePart(value ?? ''),
    'millisecond'
  )

const componentIsValid = (input: {
  readonly checkValidity: () => boolean
}): boolean => {
  try {
    return input.checkValidity()
  } catch (_error: unknown) {
    return false
  }
}

const createEditorRoot = (
  kind: 'date' | 'time' | 'datetime'
): HTMLDivElement => {
  const root = document.createElement('div')
  root.className = `facade-grid-temporal-editor facade-grid-${kind}-editor`
  root.dataset.component =
    kind === 'datetime' ? '.datetime-input' : '.grid-editor'
  root.dataset.editor = kind
  root.tabIndex = -1
  return root
}

const appendTemporalIcon = (
  input: HTMLElement,
  slot: 'clear-icon' | 'expand-icon',
  name: 'backspace' | 'calendar-blank' | 'clock'
): void => {
  const icon = iconElement(name, 'xs')
  icon.setAttribute('slot', slot)
  icon.setAttribute('aria-hidden', 'true')
  input.append(icon)
}

const prepareTemporalInput = (
  input: HTMLElement,
  expandIcon: 'calendar-blank' | 'clock'
): void => {
  // Focus entering shadow DOM is retargeted to the host. AG Grid needs an
  // explicit tabindex there to recognize that focus is still inside the editor.
  input.setAttribute('tabindex', '-1')
  input.setAttribute('size', 's')
  appendTemporalIcon(input, 'clear-icon', 'backspace')
  appendTemporalIcon(input, 'expand-icon', expandIcon)
}

const handleEditorKeydown =
  (params: TemporalEditorParams, isValid: () => boolean, close: () => void) =>
  (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      close()
      return
    }

    if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      params.validate()
      if (isValid()) {
        params.stopEditing()
      }
    }
  }

abstract class TemporalCellEditor
  implements ICellEditorComp<Record<string, unknown>, unknown, unknown>
{
  protected params?: TemporalEditorParams
  protected readonly gui: HTMLDivElement
  protected state: 'loading' | 'ready' | 'failed' | 'destroyed' = 'loading'
  private cleanup?: () => void

  protected constructor(kind: 'date' | 'time' | 'datetime') {
    this.gui = createEditorRoot(kind)
  }

  abstract configure(params: TemporalEditorParams): void
  protected abstract get focusTarget(): DateInputElement | WaTimeInput
  abstract close(): void
  abstract valid(): boolean
  protected abstract readValue(): string | null | ParsedEditorValue

  // Registers the editor immediately so AG Grid can destroy it during loading.
  init(params: TemporalEditorParams): void {
    this.params = params
    const onKeydown = handleEditorKeydown(
      params,
      () => !this.isCancelAfterEnd(),
      () => {
        if (this.state === 'ready') {
          this.close()
        }
      }
    )
    const refreshValidation = (): void => params.validate()
    this.gui.addEventListener('input', refreshValidation)
    this.gui.addEventListener('change', refreshValidation)
    this.gui.addEventListener('keydown', onKeydown)
    this.cleanup = () => {
      this.gui.removeEventListener('input', refreshValidation)
      this.gui.removeEventListener('change', refreshValidation)
      this.gui.removeEventListener('keydown', onKeydown)
    }

    const loading = loadPageComponents(this.gui)

    if (!loading) {
      this.setup(params)
      return
    }

    this.gui.setAttribute('aria-busy', 'true')
    loading
      .then(() => this.setup(params))
      .catch((error: unknown) => {
        if (this.state !== 'destroyed') {
          this.state = 'failed'
          this.gui.removeAttribute('aria-busy')
          console.error('Unable to initialize the date/time editor', error)
          params.stopEditing()
        }
      })
  }

  // Configures detached elements only after their real APIs are available.
  private setup(params: TemporalEditorParams): void {
    if (this.state === 'destroyed') {
      return
    }

    customElements.upgrade(this.gui)
    this.configure(params)
    this.state = 'ready'
    this.gui.removeAttribute('aria-busy')
    this.focusWhenReady()
  }

  // Hand focus to rendered segments only while this editor still owns it.
  private focusWhenReady(): void {
    if (this.state !== 'ready') {
      return
    }

    this.focusTarget.updateComplete
      .then(() => {
        if (
          this.state === 'ready' &&
          this.gui.isConnected &&
          this.gui.ownerDocument.activeElement === this.gui
        ) {
          this.focus()
        }
      })
      .catch(() => undefined)
  }

  // Use the control's public API to select its first editable segment.
  protected focus(): void {
    this.focusTarget.focus({ preventScroll: true })
  }

  getGui(): HTMLElement {
    return this.gui
  }

  afterGuiAttached(): void {
    this.gui.focus({ preventScroll: true })
    this.focusWhenReady()
  }

  // Keeps controls in the scrolling cell while their own pickers can still open.
  isPopup(): boolean {
    return false
  }

  isCancelAfterEnd(): boolean {
    return this.state !== 'ready' || !this.valid()
  }

  // A pending or failed control must never replace the stored cell value.
  getValue(): string | null | ParsedEditorValue {
    return this.state === 'ready' ? this.readValue() : null
  }

  getValidationElement(): HTMLElement {
    return this.gui
  }

  getValidationErrors(): string[] | null {
    return this.isCancelAfterEnd() ? ['Enter a valid value'] : null
  }

  destroy(): void {
    this.cleanup?.()
    this.cleanup = undefined
    if (this.state === 'ready') {
      this.close()
    }
    this.state = 'destroyed'
  }
}

export class FacadeDateCellEditor extends TemporalCellEditor {
  private readonly input: DateInputElement

  constructor() {
    super('date')
    this.input = document.createElement(DATE_INPUT_TAG) as DateInputElement
    this.input.dataset.component = '.date-input'
    prepareTemporalInput(this.input, 'calendar-blank')
    this.gui.append(this.input)
  }

  configure(params: TemporalEditorParams): void {
    const value = stringValue(extractEditorValue(params))
    this.input.value = value
    this.input.required = params.required ?? false
    this.input.withClear = !this.input.required
    this.input.min = datePart(params.min)
    this.input.max = datePart(params.max)
    this.input.size = 's'
  }

  // Begin date editing at the first segment in the user's locale.
  protected get focusTarget(): DateInputElement {
    return this.input
  }

  // Keep the calendar available without taking focus away from date entry.
  protected focus(): void {
    super.focus()
    this.input.show().catch(() => undefined)
  }

  close(): void {
    this.input.hide().catch(() => undefined)
  }

  valid(): boolean {
    const value = this.input.value
    const params = this.params
    return (
      (!this.input.required || value.length > 0) &&
      (value.length === 0 ||
        (hasValidDateShape(value) &&
          isWithinBounds(value, params?.min, params?.max) &&
          componentIsValid(this.input)))
    )
  }

  protected readValue(): ParsedEditorValue {
    const value = this.input.value

    return parsedEditorValue(
      this.params?.formatValue(value) ?? value,
      nullableValue(value)
    )
  }
}

export class FacadeTimeCellEditor extends TemporalCellEditor {
  private readonly input: WaTimeInput
  private precision: TemporalPrecision = 'minute'
  private milliseconds = '000'
  private step = 60

  constructor() {
    super('time')
    this.input = document.createElement('wa-time-input')
    this.input.dataset.component = '.time-input'
    prepareTemporalInput(this.input, 'clock')
    this.gui.append(this.input)
  }

  configure(params: TemporalEditorParams): void {
    const value = stringValue(extractEditorValue(params))
    this.precision = inferTemporalPrecision(value, params.precision)
    const preserved = preserveTimeMilliseconds(value)
    this.milliseconds = preserved.milliseconds
    this.step = configuredTemporalStep(params.precision, this.precision)
    this.input.step = this.step
    this.input.value =
      this.precision === 'millisecond' ? preserved.value : value
    this.input.required = params.required ?? false
    this.input.withClear = !this.input.required
    this.input.min = timePart(params.min)
    this.input.max = timePart(params.max)
    this.input.hourFormat = '24'
    this.input.size = 's'
  }

  // Begin time editing at the first empty segment, or the hour.
  protected get focusTarget(): WaTimeInput {
    return this.input
  }

  close(): void {
    this.input.hide().catch(() => undefined)
  }

  valid(): boolean {
    const value = this.normalizedValue()
    const params = this.params
    return (
      (!this.input.required || value.length > 0) &&
      (value.length === 0 ||
        (hasValidTimeShape(value) &&
          isTimeWithinBounds(
            value,
            timePart(params?.min),
            timePart(params?.max)
          ) &&
          (this.precision === 'millisecond'
            ? isTimeStepAligned(value, params?.min, this.step)
            : componentIsValid(this.input))))
    )
  }

  protected readValue(): string | null {
    return nullableValue(this.normalizedValue())
  }

  private normalizedValue(): string {
    if (this.precision !== 'millisecond') {
      return normalizeTime(this.input.value, this.precision) ?? ''
    }

    const preserved = preserveTimeMilliseconds(
      this.input.value,
      this.milliseconds
    )
    this.milliseconds = preserved.milliseconds
    return preserved.value
  }
}

export class FacadeDateTimeCellEditor extends TemporalCellEditor {
  private readonly controls = document.createElement('div')
  private readonly dateInput: DateInputElement
  private readonly timeInput: WaTimeInput
  private precision: TemporalPrecision = 'minute'
  private milliseconds = '000'
  private step = 60
  private initialValue = ''
  private edited = false
  private draftCleanup?: () => void

  constructor() {
    super('datetime')
    this.dateInput = document.createElement(DATE_INPUT_TAG) as DateInputElement
    this.timeInput = document.createElement('wa-time-input')
    this.controls.dataset.part = 'datetime-controls'
    prepareTemporalInput(this.dateInput, 'calendar-blank')
    prepareTemporalInput(this.timeInput, 'clock')
    this.controls.append(this.dateInput, this.timeInput)
    this.gui.append(this.controls)
  }

  configure(params: TemporalEditorParams): void {
    const value = stringValue(extractEditorValue(params))
    const [date = '', time = ''] = value.split('T')
    this.initialValue = value
    this.edited = false
    this.precision =
      params.precision === undefined || params.precision === 'minute'
        ? 'minute'
        : inferTemporalPrecision(time, params.precision)
    const preserved = preserveTimeMilliseconds(time)
    this.milliseconds = preserved.milliseconds

    this.dateInput.value = date
    this.dateInput.required = params.required ?? false
    this.dateInput.withClear = !this.dateInput.required
    this.dateInput.min = datePart(params.min)
    this.dateInput.max = datePart(params.max)
    this.dateInput.size = 's'

    this.timeInput.value =
      this.precision === 'millisecond'
        ? preserved.value
        : (normalizeTime(time, this.precision) ?? time)
    this.timeInput.required = params.required ?? false
    this.timeInput.withClear = !this.timeInput.required
    this.step = configuredTemporalStep(params.precision, this.precision)
    this.timeInput.step = this.step
    this.timeInput.hourFormat = '24'
    this.timeInput.size = 's'

    this.bindDraftEvents(params)
  }

  // Tracks edits, supplies the first default time, and follows date-specific bounds.
  private bindDraftEvents(params: TemporalEditorParams): void {
    this.draftCleanup?.()
    const updateTimeBounds = (): void => {
      this.timeInput.min = matchingDateTimeBound(
        this.dateInput.value,
        params.min,
        this.precision
      )
      this.timeInput.max = matchingDateTimeBound(
        this.dateInput.value,
        params.max,
        this.precision
      )
    }
    let defaultTimePending = this.initialValue === ''
    const onDateEdit = (): void => {
      this.edited = true
      if (defaultTimePending && hasValidDateShape(this.dateInput.value)) {
        this.timeInput.value = normalizeTime('12:00', this.precision) ?? '12:00'
        defaultTimePending = false
      }
      updateTimeBounds()
    }
    const onTimeEdit = (): void => {
      this.edited = true
      defaultTimePending = false
    }
    this.dateInput.addEventListener('input', onDateEdit)
    this.dateInput.addEventListener('change', onDateEdit)
    this.timeInput.addEventListener('input', onTimeEdit)
    this.timeInput.addEventListener('change', onTimeEdit)
    this.draftCleanup = () => {
      this.dateInput.removeEventListener('input', onDateEdit)
      this.dateInput.removeEventListener('change', onDateEdit)
      this.timeInput.removeEventListener('input', onTimeEdit)
      this.timeInput.removeEventListener('change', onTimeEdit)
    }
    updateTimeBounds()
  }

  // Start a combined edit in the date field before moving on to time.
  protected get focusTarget(): DateInputElement {
    return this.dateInput
  }

  close(): void {
    this.dateInput.hide().catch(() => undefined)
    this.timeInput.hide().catch(() => undefined)
  }

  valid(): boolean {
    const params = this.params
    const required = params?.required ?? false
    const date = this.dateInput.value
    const time = this.normalizedTimeValue()

    if (!(date || time)) {
      return !required
    }

    if (!(date && time)) {
      return false
    }

    const value = comparableDateTime(this.readValue())
    return (
      hasValidDateShape(date) &&
      hasValidTimeShape(time) &&
      isWithinBounds(
        value,
        comparableDateTime(params?.min),
        comparableDateTime(params?.max)
      ) &&
      componentIsValid(this.dateInput) &&
      (this.precision === 'millisecond'
        ? isTimeStepAligned(
            time,
            matchingDateTimeBound(date, params?.min),
            this.step
          )
        : componentIsValid(this.timeInput))
    )
  }

  protected readValue(): string | null {
    const date = this.dateInput.value
    const time = this.normalizedTimeValue()

    const unchanged =
      !this.edited &&
      date === datePart(this.initialValue) &&
      time === normalizeTime(timePart(this.initialValue), this.precision)
    const nextValue = date && time ? `${date}T${time}` : null

    return unchanged ? nullableValue(this.initialValue) : nextValue
  }

  private normalizedTimeValue(): string {
    if (this.precision !== 'millisecond') {
      return normalizeTime(this.timeInput.value, this.precision) ?? ''
    }

    const preserved = preserveTimeMilliseconds(
      this.timeInput.value,
      this.milliseconds
    )
    this.milliseconds = preserved.milliseconds
    return preserved.value
  }

  destroy(): void {
    this.draftCleanup?.()
    this.draftCleanup = undefined
    super.destroy()
  }
}

export type { TemporalEditorParams, TemporalPrecision }
export { isTimeStepAligned }
